/**
 * Map interaction state management
 *
 * Bridges the generic WebGL renderer with Qud-specific world map data.
 * Uses a single requestAnimationFrame loop for all updates and rendering.
 */

import type { WorldMap } from '@/qud/types';
import { WebGLContext } from '@/graphics/WebGLContext';
import { TileMapShader } from '@/graphics/TileMapShader';
import { TileMapRenderer } from '@/graphics/TileMapRenderer';
import type { GamepadController } from '@/input/GamepadController';
import { QudSpriteLoader } from './QudSpriteLoader';
import { Tooltip } from './Tooltip';
import { FrameStats } from './FrameStats';
import { CELL_WIDTH, CELL_HEIGHT } from '@/qud/sprites/constants';

/** Demo mode waypoint - a point of interest to animate to */
interface DemoWaypoint {
  name: string;
  x: number; // tile X
  y: number; // tile Y
  scale: number; // zoom level
  dwellTime: number; // ms to stay at this point
}

/** Easing function for smooth animation */
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export class MapState {
  private ctx: WebGLContext;
  private shader: TileMapShader;
  private renderer: TileMapRenderer;
  private spriteLoader: QudSpriteLoader;
  private tooltip: Tooltip;
  private frameStats: FrameStats;
  private worldMap: WorldMap | null = null;
  private canvas: HTMLCanvasElement;

  // Main loop
  private running: boolean = false;
  private animationId: number | null = null;
  private lastFrameTime: number = 0;
  private gamepadController: GamepadController | null = null;
  private boundMainLoop: () => void;

  // Input state (set by event handlers, consumed by main loop)
  private isDragging: boolean = false;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private pendingPanX: number = 0;
  private pendingPanY: number = 0;

  // Heatmap state
  private currentBlueprintName: string | null = null;
  private currentAnnotations: Map<string, number> | null = null;
  private heatmapLookup: Map<number, number> | null = null; // y*65536+x -> intensity
  private solidSpriteCell: number = -1;

  // Demo mode state
  private demoMode: boolean = false;
  private demoWaypoints: DemoWaypoint[] = [
    { name: 'Joppa', x: 11, y: 22, scale: 3, dwellTime: 2000 },
    { name: 'Six Day Stilt', x: 5, y: 2, scale: 3, dwellTime: 2000 },
    { name: 'Grit Gate', x: 22, y: 14, scale: 3, dwellTime: 2000 },
    { name: 'Bethesda Susa', x: 25, y: 3, scale: 3, dwellTime: 2000 },
    { name: 'Golgotha', x: 23, y: 9, scale: 3, dwellTime: 2000 },
    { name: 'Kyakukya', x: 27, y: 20, scale: 3, dwellTime: 2000 },
    { name: 'Fungal Jungle', x: 53, y: 17, scale: 3, dwellTime: 2000 },
    { name: 'Moon Stair', x: 75, y: 9, scale: 3, dwellTime: 2000 },
    { name: 'Full Map', x: 40, y: 12, scale: 0.8, dwellTime: 3000 },
  ];
  private currentWaypointIndex: number = 0;
  private animationStartTime: number = 0;
  private animationDuration: number = 3000; // ms for transitions
  private startView: { offsetX: number; offsetY: number; scale: number } | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = new WebGLContext(canvas);
    this.shader = new TileMapShader(this.ctx.gl);
    this.renderer = new TileMapRenderer(this.ctx, this.shader, CELL_WIDTH, CELL_HEIGHT);
    this.spriteLoader = new QudSpriteLoader();
    this.tooltip = new Tooltip();
    this.frameStats = new FrameStats();
    this.boundMainLoop = this.mainLoop.bind(this);

    // Create solid sprite for heatmap overlays
    this.solidSpriteCell = this.createSolidSprite();

    this.setupEventListeners();

    // Debug: expose on window
    // @ts-expect-error debug global
    window.debugRenderer = this.renderer;
    // @ts-expect-error debug global
    window.debugMapState = this;
  }

  /**
   * Create a solid white sprite for use as overlays.
   */
  private createSolidSprite(): number {
    const allocator = this.renderer.getAtlasAllocator();
    const data = new Uint8ClampedArray(CELL_WIDTH * CELL_HEIGHT * 4);
    for (let i = 0; i < data.length; i += 4) {
      data[i] = 255; // R - white means uses detail color in shader
      data[i + 1] = 255; // G
      data[i + 2] = 255; // B
      data[i + 3] = 255; // A
    }
    const imageData = new ImageData(data, CELL_WIDTH, CELL_HEIGHT);
    return allocator.allocateAndUpload(imageData);
  }

  /**
   * Initialize the sprite loader.
   * Must be called before setWorldMap.
   */
  async initialize(): Promise<void> {
    await this.spriteLoader.initialize();
  }

  /**
   * Check if the map state is ready (sprite loader initialized).
   */
  isReady(): boolean {
    return this.spriteLoader.isReady();
  }

  /**
   * Start the main render loop.
   */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastFrameTime = performance.now();
    this.mainLoop();
  }

  /**
   * Stop the main render loop.
   */
  stop(): void {
    this.running = false;
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Main loop - runs every frame, handles all updates and rendering.
   */
  private mainLoop(): void {
    if (!this.running) return;

    const now = performance.now();
    const deltaTime = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;

    // Start frame timing
    this.frameStats.beginFrame();

    // Poll gamepad (if set)
    if (this.gamepadController) {
      this.gamepadController.pollOnce(deltaTime);
    }

    // Process pending input
    this.processInput(deltaTime);

    // Update demo mode animation
    if (this.demoMode) {
      this.updateDemoMode(now);
    }

    // Render
    this.renderFrame();

    // End frame timing
    this.frameStats.endFrame();

    // Schedule next frame
    this.animationId = requestAnimationFrame(this.boundMainLoop);
  }

  /**
   * Process accumulated input since last frame.
   */
  private processInput(_deltaTime: number): void {
    // Apply pending pan
    if (this.pendingPanX !== 0 || this.pendingPanY !== 0) {
      this.renderer.pan(this.pendingPanX, this.pendingPanY);
      this.pendingPanX = 0;
      this.pendingPanY = 0;
    }
  }

  /**
   * Update demo mode animation state.
   */
  private updateDemoMode(now: number): void {
    const elapsed = now - this.animationStartTime;
    const waypoint = this.demoWaypoints[this.currentWaypointIndex];
    const targetOffset = this.getTileCenterOffset(waypoint.x, waypoint.y, waypoint.scale);

    if (elapsed < this.animationDuration) {
      // Animating to waypoint
      const t = easeInOutCubic(elapsed / this.animationDuration);

      if (this.startView) {
        const newOffsetX = this.startView.offsetX + (targetOffset.x - this.startView.offsetX) * t;
        const newOffsetY = this.startView.offsetY + (targetOffset.y - this.startView.offsetY) * t;
        const newScale = this.startView.scale + (waypoint.scale - this.startView.scale) * t;
        this.renderer.setView(newOffsetX, newOffsetY, newScale);
      }
    } else if (elapsed < this.animationDuration + waypoint.dwellTime) {
      // Dwelling at waypoint - ensure we're exactly at target
      this.renderer.setView(targetOffset.x, targetOffset.y, waypoint.scale);
    } else {
      // Move to next waypoint
      this.currentWaypointIndex = (this.currentWaypointIndex + 1) % this.demoWaypoints.length;
      this.startView = this.renderer.getView();
      this.animationStartTime = now;
    }
  }

  /**
   * Render a single frame.
   */
  private renderFrame(): void {
    if (!this.worldMap) return;

    const mapWidth = this.worldMap.width;
    const mapHeight = this.worldMap.height;

    // Clear canvas at start of frame (before any renderers draw)
    this.ctx.bindDefaultFramebuffer();
    this.ctx.clear(0.1, 0.1, 0.1, 1.0);

    // Set tile range for offscreen buffer sizing (entire map)
    this.renderer.setVisibleTileRange(0, 0, mapWidth, mapHeight);

    // Begin frame
    this.renderer.beginFrame();

    // Render all tiles (no visibility culling - map is small)
    for (let y = 0; y < mapHeight; y++) {
      for (let x = 0; x < mapWidth; x++) {
        const tile = this.worldMap.tiles[y]?.[x];
        if (!tile?.sprite || !tile.renderColors) continue;

        const cellIndex = this.spriteLoader.getCell(tile.sprite);
        if (cellIndex === undefined) continue;

        const fg = tile.renderColors.fg;
        const detail = tile.renderColors.detail;

        this.renderer.addInstance(
          x,
          y,
          cellIndex,
          fg[0],
          fg[1],
          fg[2],
          1.0,
          detail[0],
          detail[1],
          detail[2],
          1.0
        );
      }
    }

    // Render heatmap overlays on top of tiles
    if (this.heatmapLookup && this.heatmapLookup.size > 0) {
      for (let y = 0; y < mapHeight; y++) {
        const yOffset = y * 65536;
        for (let x = 0; x < mapWidth; x++) {
          const numericKey = yOffset + x;
          const intensity = this.heatmapLookup.get(numericKey);
          if (intensity === undefined) continue;

          const [r, g, b] = this.getHeatmapColor(intensity);
          this.renderer.addInstance(
            x,
            y,
            this.solidSpriteCell,
            0,
            0,
            0,
            0, // fg (unused - sprite is white)
            r,
            g,
            b,
            0.6 // heatmap alpha
          );
        }
      }
    }

    // End frame (WebGL render)
    this.renderer.endFrame();

    // Draw frame stats overlay
    this.frameStats.draw(this.canvas);
  }

  setWorldMap(worldMap: WorldMap): void {
    this.worldMap = worldMap;
    this.spriteLoader.loadSpritesForWorldMap(worldMap, this.renderer.getAtlasAllocator());
  }

  setGamepadController(controller: GamepadController): void {
    this.gamepadController = controller;
  }

  /**
   * Set heatmap annotations on the map
   */
  setAnnotations(blueprintName: string, data: Map<string, number>): void {
    const MIN_THRESHOLD = 1e-9;
    this.currentBlueprintName = blueprintName;
    this.currentAnnotations = data;

    this.heatmapLookup = new Map();
    data.forEach((intensity, key) => {
      if (intensity <= MIN_THRESHOLD) return;
      const [xStr, yStr] = key.split(',');
      const x = parseInt(xStr, 10);
      const y = parseInt(yStr, 10);
      const numericKey = y * 65536 + x;
      this.heatmapLookup!.set(numericKey, intensity);
    });

    if (this.heatmapLookup.size === 0) {
      this.heatmapLookup = null;
    }
  }

  /**
   * Clear all heatmap annotations
   */
  clearAnnotations(): void {
    this.currentBlueprintName = null;
    this.currentAnnotations = null;
    this.heatmapLookup = null;
  }

  /**
   * Convert heatmap intensity to RGB color (log scale gradient)
   */
  private getHeatmapColor(expectation: number): [number, number, number] {
    const clamped = Math.max(0.0001, Math.min(10, expectation));
    const logValue = Math.log10(clamped);
    const band = Math.min(4, Math.max(0, Math.floor(logValue + 4)));
    const bandProgress = logValue + 4 - band;

    const colors: [number, number, number][] = [
      [0.1, 0.2, 0.6],
      [0.0, 0.6, 0.8],
      [0.2, 0.7, 0.3],
      [0.9, 0.8, 0.2],
      [0.9, 0.4, 0.1],
      [0.8, 0.1, 0.1],
    ];

    const c1 = colors[band];
    const c2 = colors[band + 1];

    return [
      c1[0] + (c2[0] - c1[0]) * bandProgress,
      c1[1] + (c2[1] - c1[1]) * bandProgress,
      c1[2] + (c2[2] - c1[2]) * bandProgress,
    ];
  }

  private setupEventListeners(): void {
    // Mouse move for hover tooltips
    this.canvas.addEventListener('mousemove', (e) => {
      this.handleMouseMove(e);
    });

    // Mouse leave to hide tooltip
    this.canvas.addEventListener('mouseleave', () => {
      this.tooltip.hide();
    });

    // Mouse down/up/move for panning
    this.canvas.addEventListener('mousedown', (e) => {
      if (e.button === 0) {
        this.isDragging = true;
        this.lastMouseX = e.clientX;
        this.lastMouseY = e.clientY;
        this.canvas.style.cursor = 'grabbing';
      }
    });

    window.addEventListener('mouseup', () => {
      if (this.isDragging) {
        this.isDragging = false;
        this.canvas.style.cursor = 'default';
      }
    });

    window.addEventListener('mousemove', (e) => {
      if (this.isDragging) {
        const deltaX = e.clientX - this.lastMouseX;
        const deltaY = e.clientY - this.lastMouseY;
        this.lastMouseX = e.clientX;
        this.lastMouseY = e.clientY;
        // Accumulate pan - will be applied in main loop
        this.pendingPanX += deltaX;
        this.pendingPanY += deltaY;
      }
    });

    // Window resize
    window.addEventListener('resize', () => {
      this.ctx.resize();
    });

    // Wheel zoom - apply immediately centered on cursor
    this.canvas.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        const rect = this.canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        // Convert wheel delta to zoom factor (negative delta = zoom in)
        const zoomDelta = Math.exp(-e.deltaY * 0.002);
        this.renderer.zoomAt(mouseX, mouseY, zoomDelta);
      },
      { passive: false }
    );
  }

  private handleMouseMove(e: MouseEvent): void {
    if (this.isDragging || !this.worldMap) return;

    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const tileCoords = this.renderer.screenToTile(x, y, this.worldMap.width, this.worldMap.height);
    if (!tileCoords) {
      this.tooltip.hide();
      return;
    }

    const tile = this.worldMap.tiles[tileCoords.y]?.[tileCoords.x];
    if (!tile || tile.blueprints.length === 0) {
      this.tooltip.hide();
      return;
    }

    const locationKey = `${tileCoords.x},${tileCoords.y}`;
    const location = this.worldMap.locations.get(locationKey);
    if (location) {
      let heatmapInfo = undefined;
      if (this.currentBlueprintName && this.currentAnnotations) {
        const key = `${tileCoords.x},${tileCoords.y}`;
        const expectedSpawns = this.currentAnnotations.get(key);
        if (expectedSpawns !== undefined) {
          heatmapInfo = {
            blueprintName: this.currentBlueprintName,
            expectedSpawns,
          };
        }
      }
      this.tooltip.show(location, e.clientX, e.clientY, heatmapInfo);
    } else {
      this.tooltip.hide();
    }
  }

  // ============ Demo Mode ============

  startDemoMode(): void {
    if (this.demoMode) return;
    this.demoMode = true;
    this.currentWaypointIndex = 0;
    this.startView = this.renderer.getView();
    this.animationStartTime = performance.now();
  }

  stopDemoMode(): void {
    this.demoMode = false;
  }

  toggleDemoMode(): void {
    if (this.demoMode) {
      this.stopDemoMode();
    } else {
      this.startDemoMode();
    }
  }

  isDemoModeActive(): boolean {
    return this.demoMode;
  }

  toggleStats(): void {
    this.frameStats.toggle();
  }

  // ============ Public API for external input (gamepad) ============

  /**
   * Pan the map. Called from gamepad controller.
   */
  pan(deltaX: number, deltaY: number): void {
    this.pendingPanX += deltaX;
    this.pendingPanY += deltaY;
  }

  /**
   * Zoom the map centered on screen. Called from gamepad controller.
   */
  zoomBy(factor: number): void {
    this.renderer.zoomBy(factor);
  }

  /**
   * Trigger a resize.
   */
  resize(): void {
    this.ctx.resize();
  }

  setAAEnabled(enabled: boolean): void {
    this.renderer.setAAEnabled(enabled);
  }

  isAAEnabled(): boolean {
    return this.renderer.isAAEnabled();
  }

  /**
   * Get frame stats for external display.
   */
  getFrameStats(): { fps: number; frameTime: number; histogram: number[] } {
    return this.frameStats.getStats();
  }

  private getTileCenterOffset(
    tileX: number,
    tileY: number,
    scale: number
  ): { x: number; y: number } {
    const { width, height } = this.ctx.getDimensions();
    const tilePixelX = tileX * CELL_WIDTH * scale + (CELL_WIDTH * scale) / 2;
    const tilePixelY = tileY * CELL_HEIGHT * scale + (CELL_HEIGHT * scale) / 2;
    return {
      x: width / 2 - tilePixelX,
      y: height / 2 - tilePixelY,
    };
  }
}
